// src/components/Background.jsx
import React, { useEffect, useRef, useState } from 'react';
import '../styles/Background.css';
import MobileBackground from './MobileBackground';

/**
 * Enhanced metaballs background with:
 * - Color variety (blues, purples, cyans, magentas)
 * - Size variation and subtle pulsing
 * - Parallax scrolling effect
 * - Optimized for performance
 * - Conditionally renders lightweight version on mobile/tablet
 */
export default function Background({ variant = 'default' }) {
	const canvasRef = useRef(null);
	const blobsRef = useRef([]);
	const rafRef = useRef(null);
	const scrollYRef = useRef(0);
	const [isMobileDevice, setIsMobileDevice] = useState(false);

	// Detect mobile/tablet devices
	useEffect(() => {
		const checkIfMobile = () => {
			const width = window.innerWidth;
			setIsMobileDevice(width <= 1024); // Mobile and tablet
		};

		checkIfMobile();
		window.addEventListener('resize', checkIfMobile);

		return () => window.removeEventListener('resize', checkIfMobile);
	}, []);

	useEffect(() => {
		// If mobile/tablet, skip canvas initialization
		if (isMobileDevice) {
			return;
		}

		const canvas = canvasRef.current;
		if (!canvas) return;

		const ctx = canvas.getContext('2d', { alpha: false });
		const isMobile = window.innerWidth < 768;
		const isLowEnd = /Android|webOS/i.test(navigator.userAgent);

		// Performance settings
		const BLOB_COUNT = isMobile ? 5 : isLowEnd ? 6 : 12;
		const TARGET_FPS = isMobile ? 24 : 30;
		const frameDelay = 1000 / TARGET_FPS;

		let width = window.innerWidth;
		let height = window.innerHeight;
		let lastFrame = 0;

		function resize() {
			width = window.innerWidth;
			height = window.innerHeight;
			canvas.width = width;
			canvas.height = height;
		}

		resize();
		window.addEventListener('resize', resize);

		// Color palette: blues, purples, cyans, magentas
		const colorPalettes = [
			{ hue: 200, sat: 85, name: 'cyan-blue' }, // Cyan-blue
			{ hue: 220, sat: 80, name: 'blue' }, // Deep blue
			{ hue: 240, sat: 85, name: 'indigo' }, // Indigo
			{ hue: 270, sat: 80, name: 'purple' }, // Purple
			{ hue: 290, sat: 85, name: 'magenta' }, // Magenta
			{ hue: 180, sat: 90, name: 'cyan' }, // Bright cyan
			{ hue: 260, sat: 75, name: 'violet' }, // Violet
		];

		// Initialize blobs with varied properties
		function initBlobs() {
			blobsRef.current = Array.from({ length: BLOB_COUNT }, (_, i) => {
				const color = colorPalettes[i % colorPalettes.length];
				const baseSize = isMobile ? 280 : 420;
				const sizeVariation = Math.random() * (isMobile ? 300 : 500);

				return {
					x: Math.random() * width,
					y: Math.random() * height,
					baseX: 0, // Will be set after x is determined
					baseY: 0,
					vx: (Math.random() - 0.5) * (isMobile ? 0.4 : 0.7),
					vy: (Math.random() - 0.5) * (isMobile ? 0.4 : 0.7),
					radius: baseSize + sizeVariation,
					baseRadius: baseSize + sizeVariation,
					hue: color.hue + (Math.random() - 0.5) * 20, // Add variation
					sat: color.sat + (Math.random() - 0.5) * 10,
					// Parallax depth: 0.1 to 0.4 (lower = slower movement)
					parallaxDepth: 0.15 + Math.random() * 0.25,
					// Pulse properties
					pulseSpeed: 0.001 + Math.random() * 0.002,
					pulsePhase: Math.random() * Math.PI * 2,
					pulseAmount: 0.08 + Math.random() * 0.12,
					// Opacity variation
					opacity: 0.12 + Math.random() * 0.06,
				};
			});

			// Set base positions
			blobsRef.current.forEach(blob => {
				blob.baseX = blob.x;
				blob.baseY = blob.y;
			});
		}

		initBlobs();

		// Handle scroll for parallax
		function handleScroll() {
			scrollYRef.current = window.pageYOffset || document.documentElement.scrollTop;
		}

		window.addEventListener('scroll', handleScroll, { passive: true });

		// Update blob positions and properties
		function updateBlobs() {
			const time = performance.now() * 0.001;

			blobsRef.current.forEach(blob => {
				// Normal movement
				blob.x += blob.vx;
				blob.y += blob.vy;

				// Bounce off edges with wrapping
				if (blob.x < -blob.radius || blob.x > width + blob.radius) {
					blob.x = blob.x < 0 ? width + blob.radius : -blob.radius;
					blob.baseX = blob.x;
				}
				if (blob.y < -blob.radius || blob.y > height + blob.radius) {
					blob.y = blob.y < 0 ? height + blob.radius : -blob.radius;
					blob.baseY = blob.y;
				}

				// Subtle pulsing
				const pulse = Math.sin(time * blob.pulseSpeed + blob.pulsePhase);
				blob.radius = blob.baseRadius * (1 + pulse * blob.pulseAmount);
			});
		}

		// Draw blobs with enhanced visuals
		function draw() {
			// Clear with background color
			ctx.fillStyle = '#0b0f17';
			ctx.fillRect(0, 0, width, height);

			// Apply parallax offset based on scroll
			const parallaxOffset = scrollYRef.current * 0.3;

			// Draw each blob with parallax
			blobsRef.current.forEach(blob => {
				// Calculate parallax position
				const parallaxY = blob.y - parallaxOffset * blob.parallaxDepth;

				// Main blob gradient
				const gradient = ctx.createRadialGradient(blob.x, parallaxY, 0, blob.x, parallaxY, blob.radius);

				gradient.addColorStop(0, `hsla(${blob.hue}, ${blob.sat}%, 70%, ${blob.opacity})`);
				gradient.addColorStop(0.4, `hsla(${blob.hue}, ${blob.sat - 5}%, 60%, ${blob.opacity * 0.6})`);
				gradient.addColorStop(1, `hsla(${blob.hue}, ${blob.sat - 10}%, 50%, 0)`);

				ctx.fillStyle = gradient;
				ctx.beginPath();
				ctx.arc(blob.x, parallaxY, blob.radius, 0, Math.PI * 2);
				ctx.fill();
			});

			// Add brighter center overlays with screen blend
			ctx.globalCompositeOperation = 'screen';
			blobsRef.current.forEach(blob => {
				const parallaxY = blob.y - parallaxOffset * blob.parallaxDepth;

				const innerGradient = ctx.createRadialGradient(blob.x, parallaxY, 0, blob.x, parallaxY, blob.radius * 0.35);

				innerGradient.addColorStop(0, `hsla(${blob.hue + 15}, ${blob.sat + 5}%, 75%, ${blob.opacity * 0.8})`);
				innerGradient.addColorStop(0.6, `hsla(${blob.hue + 10}, ${blob.sat}%, 65%, ${blob.opacity * 0.3})`);
				innerGradient.addColorStop(1, `hsla(${blob.hue}, ${blob.sat - 5}%, 55%, 0)`);

				ctx.fillStyle = innerGradient;
				ctx.beginPath();
				ctx.arc(blob.x, parallaxY, blob.radius * 0.35, 0, Math.PI * 2);
				ctx.fill();
			});
			ctx.globalCompositeOperation = 'source-over';

			// Optional: Add subtle "lighten" layer for depth
			ctx.globalCompositeOperation = 'lighten';
			ctx.globalAlpha = 0.3;
			blobsRef.current.forEach(blob => {
				const parallaxY = blob.y - parallaxOffset * blob.parallaxDepth;

				const glowGradient = ctx.createRadialGradient(blob.x, parallaxY, 0, blob.x, parallaxY, blob.radius * 0.5);

				glowGradient.addColorStop(0, `hsla(${blob.hue + 20}, 90%, 80%, 0.15)`);
				glowGradient.addColorStop(1, 'transparent');

				ctx.fillStyle = glowGradient;
				ctx.beginPath();
				ctx.arc(blob.x, parallaxY, blob.radius * 0.5, 0, Math.PI * 2);
				ctx.fill();
			});
			ctx.globalAlpha = 1;
			ctx.globalCompositeOperation = 'source-over';
		}

		// Animation loop with frame limiting
		function animate(timestamp) {
			if (timestamp - lastFrame >= frameDelay) {
				updateBlobs();
				draw();
				lastFrame = timestamp;
			}
			rafRef.current = requestAnimationFrame(animate);
		}

		// Start animation after brief delay
		const startDelay = setTimeout(() => {
			rafRef.current = requestAnimationFrame(animate);
		}, 100);

		// Pause when tab is hidden
		function handleVisibility() {
			if (document.hidden) {
				if (rafRef.current) {
					cancelAnimationFrame(rafRef.current);
					rafRef.current = null;
				}
			} else {
				if (!rafRef.current) {
					lastFrame = performance.now();
					rafRef.current = requestAnimationFrame(animate);
				}
			}
		}

		document.addEventListener('visibilitychange', handleVisibility);

		// Cleanup
		return () => {
			clearTimeout(startDelay);
			if (rafRef.current) {
				cancelAnimationFrame(rafRef.current);
			}
			window.removeEventListener('resize', resize);
			window.removeEventListener('scroll', handleScroll);
			document.removeEventListener('visibilitychange', handleVisibility);
		};
	}, [variant, isMobileDevice]);

	// If mobile/tablet, render the lightweight version
	if (isMobileDevice) {
		return <MobileBackground />;
	}

	return (
		<div className="cosmicBG">
			<canvas ref={canvasRef} className="cosmicCanvas" />
		</div>
	);
}
